<template>
  <v-sheet  style="min-width:95vw; max-width:95vw">
    <v-toolbar flat>
      <v-toolbar-title>
        Last Submission: {{ lastAutoRedemptionPeriod }}
      </v-toolbar-title>
      <v-btn class="ma-2" variant="outlined" prepend-icon="mdi-download" @click="getLastAutoRedemptionResult" aria-label="Result Report">
        Result
      </v-btn>      
      <v-spacer></v-spacer>    
      <v-btn class="ma-2" variant="outlined" prepend-icon="mdi-upload" @click="loadData" aria-label="Generate Report">
        Generate
      </v-btn>
      <v-btn class="ma-2" variant="outlined" prepend-icon="mdi-download" @click="download" aria-label="Review Report">
        Review
      </v-btn>
      <v-spacer></v-spacer>
      <v-switch v-if="systemStore.userDetail.superUser"
        class="mt-5 me-5"
        v-model="openAutoRedemptions"
        label="Open Redemptions"
        color="blue"
        @update:modelValue="autoRedemptionStateChanged"
      ></v-switch>   
      <v-btn v-if="systemStore.userDetail.superUser && enableSubmit"
        class="ma-2" 
        variant="outlined" 
        prepend-icon="mdi-check-all"
        @click="submitAutoRedemptions">
        Submit
      </v-btn>
    </v-toolbar>

    <v-data-table v-if="items.length > 0"
      :headers="headers"
      :items="items"
      item-value="eVoucherId"
      density="compact"
    >
      <template v-slot:item.title="{ value }">
        <v-chip :text="value" border="thin opacity-25" prepend-icon="mdi-book" label>
          <template v-slot:prepend>
            <v-icon color="medium-emphasis"></v-icon>
          </template>
        </v-chip>
      </template>

      <template v-slot:item.openingBalance="{item}">
          <div class="text-right">{{ $filters.currencyUSD(item.openingBalance) }}</div>
      </template>

      <template v-slot:item.rewardsRedeemed="{item}">
          <div class="text-right">{{ $filters.currencyUSD(item.rewardsRedeemed) }}</div>
      </template>

      <template v-slot:item.balance="{item}">
          <div class="text-right">{{ $filters.currencyUSD(item.balance) }}</div>
      </template>

      <template v-slot:item.banked="{item}">
          <div class="text-right">{{ $filters.currencyUSD(item.banked) }}</div>
      </template>

      <template v-slot:item.closingBalance="{item}">
          <div class="text-right">{{ $filters.currencyUSD(item.closingBalance) }}</div>
      </template>

      <template v-slot:item.localAmount="{item}">
          <div class="text-right">{{ $filters.toDecimal(item.localAmount) }}</div>
      </template>

      <template v-slot:item.expiryDate="{item}">
          <div class="text-left">{{ item.expiryDate.split('T')[0] }}</div>
      </template>

    </v-data-table>
  </v-sheet>
</template>

<script setup>
  import { onMounted, ref } from 'vue'
  import { useSystemStore } from '@/stores/system'
  import { useExcelStore } from '@/stores/excel'

  const systemStore = useSystemStore()
  const excelStore = useExcelStore()

  const headers = ref([
    { title: 'Quarter', key: 'quarter', align: 'start' },
    { title: 'Partner Id', key: 'partnerId', align: 'start' },
    { title: 'Partner Name', key: 'partnerName', align: 'start' },
    { title: 'Partner Country', key: 'partnerCountry', align: 'start' },
    { title: 'Partner User Id', key: 'partnerUserId', align: 'start' },
    { title: 'Distributor Id', key: 'distributorId', align: 'start' },
    { title: 'Distributor Name', key: 'distributorName', align: 'start' },
    { title: 'Distributor Country', key: 'distributorCountry', align: 'start' },
    { title: 'Distributor Allocation', key: 'distributorAllocation', align: 'start' },
    { title: 'Opening Balance', key: 'openingBalance', align: 'end' },
    { title: 'Rewards Redeemed', key: 'rewardsRedeemed', align: 'end' },
    { title: 'Balance', key: 'balance', align: 'end' },
    { title: 'Banked', key: 'banked', align: 'end' },
    { title: 'Closing Balance', key: 'closingBalance', align: 'end' },
    { title: 'Local Currency', key: 'localCurrency', align: 'end' },
    { title: 'Local Amount', key: 'localAmount', align: 'end' },    
    { title: 'Expiry Date', key: 'expiryDate', align: 'start' },
    { title: 'Partner Email', key: 'partnerEmail', align: 'start' },
    { title: 'Distributor Email', key: 'distributorEmail', align: 'start' },
    { title: 'Auto Redemption', key: 'autoRedemption', align: 'start' }
  ])

  const items = ref([])
  const lastResult = ref([])
  const enableSubmit = ref(false)

  const openAutoRedemptions = ref(false)
  const lastAutoRedemptionPeriod = ref('')

  const getLastAutoRedemptionPeriod = () => {
    systemStore.getLastAutoRedemptionPeriod().then(result => {
      lastAutoRedemptionPeriod.value = result.responseData
    }).catch(error => {
      console.log('getLastAutoRedemptionPeriod', error)
    })
  }

  const getLastAutoRedemptionResult = () => {
    systemStore.getLastAutoRedemptionResult().then(result => {
      lastResult.value = result.lastAutoRedemptionResult

      downloadResult()
    }).catch(error => {
      console.log('getLastAutoRedemptionResult', error)
    })
  }

  const autoRedemptionStateChanged = (value) => {
    systemStore.setAutoRedemptionState(value).then(result => {
      openAutoRedemptions.value = result.autoRedemptionState

      if (openAutoRedemptions.value === true) {
        systemStore.showMessage('Redemptions are OPEN', { color: 'success' })
      }
      else {
        systemStore.showMessage('Redemptions are CLOSED', { color: 'warning' })
      }
    }).catch(error => {
      console.log('autoRedemptionStateChanged', error)
    })
  }

  const download = () => {
    if (items.value.length === 0) {
        return
    }

    excelStore.generateInMemoryExcelDocument(items.value, 'AutoRedemptionsDocument').catch(error => {
        console.log('Generate In Memory Excel Document - AutoRedemptionsDocument', error)
    })
  }

  const downloadResult = () => {
    if (lastResult.value.length === 0) {
        return
    }

    excelStore.generateInMemoryExcelDocument(lastResult.value, 'AutoRedemptionResultDocument').catch(error => {
        console.log('Generate In Memory Excel Document - AutoRedemptionResultDocument', error)
    })
  }

  const loadData = () => {
    systemStore.getAutoRedemptions().then(result => {
      items.value = result.autoRedemptions

      enableSubmit.value = items.value.some(item => item.autoRedemption === true);

    }).catch(error => {
      console.log('Get Auto Redemption Report', error)
    })
  }

  const submitAutoRedemptions = () => {
    if (items.value.length === 0) {
        return
    }

    enableSubmit.value = false

    systemStore.submitAutoRedemptions(items.value).then(result => {
      items.value = []
      getLastAutoRedemptionPeriod()
    }).catch(error => {
      console.log('submitAutoRedemptions', error)
    })
  }

  onMounted(() => {
    systemStore.getAutoRedemptionState().then(result => {
      openAutoRedemptions.value = result.autoRedemptionState
    }).catch(error => {
      console.log('getAutoRedemptionState', error)
    })

    getLastAutoRedemptionPeriod()
  })

</script>

<style scoped>

</style>